import type { ReactNode } from 'react';
import './remixicon.css';
import type { ApiMeta } from './api';
import { useI18n } from './i18n';

/**
 * 数据口径徽标。这是整套规范的技术抓手：
 * 任何数据面板都必须能看出「是什么数据、截止到什么时候、从哪来」。
 */
export function DataBadges({ meta }: { meta: ApiMeta | null }) {
  const { t } = useI18n();
  if (!meta) {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-danger/30 bg-danger/15 px-2 py-0.5 text-xs text-danger-light">
        <i className="ri-error-warning-line text-xs" />
        <span>{t('meta.missing')}</span>
      </span>
    );
  }
  const badge = meta.isMock
    ? { text: t('meta.mock'), cls: 'bg-muted/15 text-muted-light border border-muted/20', icon: 'ri-information-line' }
    : meta.confidence === 'forecast'
      ? { text: t('meta.forecast'), cls: 'bg-warn/15 text-warn-light border border-warn/30', icon: 'ri-line-chart-line' }
      : { text: t('meta.actual'), cls: 'bg-policy/15 text-policy-light border border-policy/30', icon: 'ri-shield-check-line' };

  return (
    <span className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 ${badge.cls}`}>
        <i className={`${badge.icon} text-xs`} />
        <span>{badge.text}</span>
      </span>
      <span className="text-muted">
        {t('meta.asOf')} {meta.asOf}
      </span>
      <span className="text-muted">
        · {t('meta.source')}: {meta.source}
      </span>
    </span>
  );
}

export function DataPanel({
  title,
  subtitle,
  meta,
  children,
  className = '',
  right,
}: {
  title: string;
  subtitle?: string;
  meta: ApiMeta | null;
  children: ReactNode;
  className?: string;
  right?: ReactNode;
}) {
  return (
    <section className={`rounded-xl border border-line bg-surface p-5 ${className}`}>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-body">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          {right}
          <DataBadges meta={meta} />
        </div>
      </header>
      {children}
    </section>
  );
}

/** 关键指标卡片。假设类数字必须提供说明入口。 */
export function KpiCard({
  label,
  value,
  hint,
  tone = 'data',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'data' | 'policy' | 'warn' | 'plain';
}) {
  const accent = {
    data: 'text-primary-light',
    policy: 'text-policy',
    warn: 'text-warn',
    plain: 'text-body',
  }[tone];

  return (
    <div className="rounded-lg border border-line bg-surface-2 p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className={`tnum mt-1 text-2xl font-semibold ${accent}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

/** 状态标签。颜色与文字同时表达，不单靠颜色区分。 */
export function StatusTag({
  tone,
  children,
}: {
  tone: 'a' | 'b' | 'c' | 'ok' | 'warn' | 'muted';
  children: ReactNode;
}) {
  const map = {
    a: 'bg-policy/20 text-policy-light border-policy/30',
    b: 'bg-primary/20 text-primary-light border-primary/30',
    c: 'bg-muted/15 text-muted-light border-muted/30',
    ok: 'bg-policy/20 text-policy-light border-policy/30',
    warn: 'bg-warn/20 text-warn-light border-warn/30',
    muted: 'bg-muted/15 text-muted-light border-muted/30',
  } as const;
  return (
    <span className={`inline-block rounded border px-2 py-0.5 text-xs whitespace-nowrap ${map[tone]}`}>
      {children}
    </span>
  );
}

/** 页脚免责声明。全站统一文案。 */
export function DisclaimerFooter({ site }: { site: string }) {
  const { t } = useI18n();
  return (
    <footer className="mt-10 border-t border-line pt-5 text-xs leading-relaxed text-muted">
      <p>{t('footer.line1')}</p>
      <p className="mt-1">{t('footer.line2')}</p>
      <p className="mt-2 text-muted/70">
        {site}
      </p>
    </footer>
  );
}
